import React from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_FIELD_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DashboardLayoutDialogFooter } from "@/views/Dashboard/Dashboard/components/DashboardLayoutDialogFooter";

export const REMOVE_BROKER_ACCOUNT_CONFIRM_PHRASE = "delete";

export type RemoveBrokerConnectedAccountTarget = {
  displayName: string;
};

export interface RemoveBrokerConnectedAccountDialogProps {
  target: RemoveBrokerConnectedAccountTarget | null;
  confirmText: string;
  onConfirmTextChange: (value: string) => void;
  isActiveInSidebar: boolean;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function RemoveBrokerConnectedAccountDialog({
  target,
  confirmText,
  onConfirmTextChange,
  isActiveInSidebar,
  isDeleting,
  onCancel,
  onConfirm,
}: RemoveBrokerConnectedAccountDialogProps) {
  const accountName = target?.displayName ?? "";
  const confirmMatches =
    confirmText.trim().toLowerCase() === REMOVE_BROKER_ACCOUNT_CONFIRM_PHRASE;

  const handleOpenChange = (open: boolean) => {
    if (!open && !isDeleting) onCancel();
  };

  return (
    <StrategyPineDetailDialog
      open={!!target}
      onOpenChange={handleOpenChange}
      title="Remove account data"
      showHeader={false}
      showDismissButton={false}
      contentClassName="sm:max-w-md"
      bodyClassName="flex-none overflow-visible"
      footer={
        <DashboardLayoutDialogFooter onCancel={onCancel}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-trade-log-action="delete"
            className="shrink-0 border-destructive/40 text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/60 focus-visible:ring-destructive/25 dark:border-destructive/50 dark:hover:bg-destructive/15 [&_svg]:text-current"
            disabled={!confirmMatches || isDeleting}
            aria-busy={isDeleting || undefined}
            onClick={onConfirm}
          >
            {isDeleting ? (
              <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <span className="normal-case tracking-normal">Remove account data</span>
            )}
          </Button>
        </DashboardLayoutDialogFooter>
      }
    >
      {target ? (
        <Field className="gap-2">
          <FieldLabel htmlFor="broker-remove-account-confirm" className={BROKER_ALERT_TITLE_CLASS}>
            Delete All Account Data
          </FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            This deletes all Tessera data for{" "}
            <span className="font-medium text-[#6b7280]">{accountName}</span>, including trades,
            milestones, layouts, and reports. It does not close your brokerage account or remove
            saved API credentials.
            {isActiveInSidebar
              ? " This is your active account. Tessera will pick another account after removal."
              : null}{" "}
            Enter &lsquo;delete&rsquo; to confirm.
          </FieldDescription>
          <Input
            id="broker-remove-account-confirm"
            value={confirmText}
            onChange={(event) => onConfirmTextChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape" && !isDeleting) onCancel();
            }}
            placeholder="Type 'delete' to confirm"
            autoFocus
            disabled={isDeleting}
            autoComplete="off"
            data-font="primary"
          />
        </Field>
      ) : null}
    </StrategyPineDetailDialog>
  );
}
